import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import styled from 'styled-components';

import {
  Currency,
  Rarity,
  SeasonEligibility,
} from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { StatsRarity } from '@sorare/core/src/lib/playerSearchView';

import { SelectCurrency } from 'atoms/SelectCurrency';
import { SelectRarity } from 'atoms/SelectRarity';
import { SelectSeasonality } from 'atoms/SelectSeasonality';
import { TimeframeSelector } from 'atoms/TimeframeSelector';
import {
  SeasonEligibilityOptions,
  seasonEligibilityFromOptions,
} from 'lib/seasonalities';
import {
  TIME_FRAME_FROM_TO,
  Timeframe,
  getTimeframeFromRarity,
} from 'lib/timeframe';

import { PriceGraph } from '../PriceGraph';
import type {
  LazyPriceGraphQuery,
  LazyPriceGraphQueryVariables,
} from './__generated__/index.graphql';

const Filters = styled(Horizontal)`
  overflow: auto;
  justify-content: flex-end;
`;

const LAZY_PLAYER_PRICE_GRAPH_QUERY = gql`
  query LazyPriceGraphQuery(
    $playerSlug: String!
    $rarity: Rarity!
    $seasonEligibility: SeasonEligibility
    $from: ISO8601DateTime
    $to: ISO8601DateTime
  ) {
    tokens {
      tokenPrices(
        first: 20
        playerSlug: $playerSlug
        rarity: $rarity
        seasonEligibility: $seasonEligibility
        from: $from
        to: $to
        includePrivateSales: true
      ) {
        id
        ...PriceGraph_tokenPrice
      }
    }
  }
  ${PriceGraph.fragments.tokenPrice}
` as TypedDocumentNode<LazyPriceGraphQuery, LazyPriceGraphQueryVariables>;

const filterableRarities = [
  Rarity.limited,
  Rarity.rare,
  Rarity.super_rare,
  Rarity.unique,
];

type Props = {
  small?: boolean;
  playerSlug: string;
  currency?: Currency;
  filters?: {
    seasonEligibility?: SeasonEligibilityOptions;
    withSeasonEligibility?: boolean;
    rarity?: StatsRarity;
    timeframe?: Timeframe;
  };
  withLegend?: boolean;
};

export const LazyPriceGraph = ({
  small,
  playerSlug,
  currency: currencyProps,
  filters,
  withLegend,
}: Props) => {
  const { currency: userCurrency } = useCurrentUserContext();
  const currency = currencyProps || userCurrency;
  const [currencyState, setCurrencyState] = useState<Currency>(currency);

  const [rarityState, setRarityState] = useState<StatsRarity>(Rarity.limited);
  const rarity: StatsRarity = filters?.rarity || rarityState;

  const [seasonEligibilityState, setSeasonEligibilityState] =
    useState<SeasonEligibilityOptions>(SeasonEligibility.IN_SEASON);
  const seasonEligibility = seasonEligibilityFromOptions(
    (filters?.withSeasonEligibility &&
      (filters?.seasonEligibility || seasonEligibilityState)) ||
      undefined
  );

  const [timeframeState, setTimeframeState] = useState<Timeframe>(
    getTimeframeFromRarity(rarity)
  );
  const timeframe = filters?.timeframe || timeframeState;
  const { from, to } = TIME_FRAME_FROM_TO[timeframe];

  const { data, loading } = useQuery(LAZY_PLAYER_PRICE_GRAPH_QUERY, {
    variables: {
      playerSlug,
      rarity,
      seasonEligibility,
      to,
      from,
    },
  });

  const hasTimeFilter = !filters?.timeframe && !!timeframe;
  const hasSeasonEligibilityFilter =
    !filters?.seasonEligibility &&
    !!filters?.withSeasonEligibility &&
    !!seasonEligibility;
  const hasRarityFilter = !filters?.rarity && !!rarity;
  const hasCurrencyFilter = !currencyProps && !!currencyState;
  const hasFilters =
    hasTimeFilter ||
    hasSeasonEligibilityFilter ||
    hasRarityFilter ||
    hasCurrencyFilter;

  return (
    <>
      {!!hasFilters && (
        <Filters>
          {!!hasTimeFilter && (
            <div>
              <TimeframeSelector
                value={timeframe}
                onChange={setTimeframeState}
                buttonSize="compact"
              />
            </div>
          )}
          {!!hasSeasonEligibilityFilter && (
            <div>
              <SelectSeasonality
                seasonality={seasonEligibility}
                setSeasonality={setSeasonEligibilityState}
                buttonSize="compact"
              />
            </div>
          )}
          {!!hasRarityFilter && (
            <div>
              <SelectRarity
                selectedRarity={rarity}
                onChange={setRarityState}
                rarities={filterableRarities}
                buttonSize="compact"
              />
            </div>
          )}
          {!!hasCurrencyFilter && (
            <div>
              <SelectCurrency
                selectedCurrency={currency}
                onChange={value => setCurrencyState(value)}
              />
            </div>
          )}
        </Filters>
      )}
      <PriceGraph
        currency={currency}
        loading={loading}
        tokenPrices={data?.tokens.tokenPrices || []}
        withLegend={withLegend}
        small={small}
        from={from}
        to={to}
      />
    </>
  );
};
